import type { InteractionType } from '@atlaskit/react-ufo/common';

import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

import { observeElementDimensions, type ElementDimensions } from './observeElementDimensions.ts';

type Ecosystem = 'connect' | 'forge';
type Scope = Readonly<Record<Ecosystem, boolean>>;
type Channel = 'UFO' | 'operational' | 'both';
type PageLoadType = 'initial' | 'transition' | 'both';
type Configuration = Scope & { channel: Channel; pageLoadType: PageLoadType };
type Registration = { ecosystem: Ecosystem; extensionKey: string; dimensions?: ElementDimensions };
type ContentMacroDimensions = Partial<Record<Ecosystem, Record<string, ElementDimensions[]>>>;
const DISABLED: Configuration = {
	connect: false,
	forge: false,
	channel: 'UFO',
	pageLoadType: 'both',
};
const MAX_REPORTED_MACROS = 50;
const registrations = new Map<Element, Registration>();
let cachedValue: string | undefined;
let cachedConfig = DISABLED;

/** Static configurations carry object values as JSON strings. Only explicit true enables a scope. */
function getConfiguration(): Configuration {
	const value = StatsigConfigurations.getString(
		'confluence_content_macro_measurements_config',
		'{}',
	);
	if (value !== cachedValue) {
		cachedValue = value;
		cachedConfig = DISABLED;
		try {
			const config: unknown = JSON.parse(value);
			if (config && typeof config === 'object') {
				const channel = 'channel' in config ? config.channel : 'UFO';
				const pageLoadType = 'pageLoadType' in config ? config.pageLoadType : 'both';
				if (
					(channel === 'UFO' || channel === 'operational' || channel === 'both') &&
					(pageLoadType === 'initial' || pageLoadType === 'transition' || pageLoadType === 'both')
				) {
					cachedConfig = {
						connect: 'connect' in config && config.connect === true,
						forge: 'forge' in config && config.forge === true,
						channel,
						pageLoadType,
					};
				}
			}
		} catch {
			// An absent or malformed configuration disables collection.
		}
	}
	return cachedConfig;
}

export function getContentMacroMeasurementScope(): Scope {
	const { connect, forge } = getConfiguration();
	return { connect, forge };
}

/** Cache UFO dimensions immediately; optionally report one settled operational measurement. */
export function observeContentMacroDimensions(
	element: Element,
	ecosystem: Ecosystem,
	extensionKey: string,
	report: (dimensions: ElementDimensions) => void,
): (() => void) | undefined {
	const config = getConfiguration();
	if (!config[ecosystem] || !extensionKey) {
		return;
	}
	const ufo = config.channel !== 'operational';
	const registration: Registration = { ecosystem, extensionKey };
	const stop = observeElementDimensions(element, (dimensions) => {
		registration.dimensions = dimensions;
	});
	if (!stop) {
		return;
	}
	if (ufo) registrations.set(element, registration);
	const timer =
		config.channel !== 'UFO'
			? setTimeout(() => {
					if (!ufo) stop();
					const current = getConfiguration();
					if (
						registration.dimensions &&
						element.isConnected &&
						current[ecosystem] &&
						current.channel !== 'UFO'
					) {
						report(registration.dimensions);
					}
				}, 10000)
			: undefined;
	return () => {
		clearTimeout(timer);
		stop();
		if (registrations.get(element) === registration) registrations.delete(element);
	};
}

/** Snapshot mounted macro instances at UFO payload construction, independently of the banner gate. */
export function getContentMacroDimensions(interactionType: InteractionType): {
	contentMacroDimensions?: ContentMacroDimensions;
	contentMacroDimensionsOmittedCount?: number;
} {
	if (interactionType !== 'page_load' && interactionType !== 'transition') return {};
	const config = getConfiguration();
	if ((!config.connect && !config.forge) || config.channel === 'operational') return {};
	if (
		(config.pageLoadType === 'initial' && interactionType !== 'page_load') ||
		(config.pageLoadType === 'transition' && interactionType !== 'transition')
	)
		return {};
	const groups = new Map<Ecosystem, Map<string, ElementDimensions[]>>();
	let reported = 0;
	let omitted = 0;
	for (const [element, { ecosystem, extensionKey, dimensions }] of registrations) {
		if (!element.isConnected || !dimensions || !config[ecosystem]) continue;
		if (reported >= MAX_REPORTED_MACROS) {
			omitted++;
			continue;
		}
		reported++;
		const group = groups.get(ecosystem) ?? new Map<string, ElementDimensions[]>();
		const instances = group.get(extensionKey) ?? [];
		instances.push({ ...dimensions });
		group.set(extensionKey, instances);
		groups.set(ecosystem, group);
	}
	return {
		contentMacroDimensions: Object.fromEntries(
			Array.from(groups, ([ecosystem, group]) => [ecosystem, Object.fromEntries(group)]),
		),
		...(omitted ? { contentMacroDimensionsOmittedCount: omitted } : {}),
	};
}
